import React, { useMemo, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import {
  RECEIVING_WORKFLOW_CARD_OPTIONS,
  RECEIVING_WORKFLOW_ONSITE_CAPTURE,
  RECEIVING_WORKFLOW_ONSITE_TO_WAREHOUSE,
  RECEIVING_WORKFLOW_ONSITE_MANIFEST,
} from "./projectReceivingWorkflow";

const iconMap = {
  [RECEIVING_WORKFLOW_ONSITE_TO_WAREHOUSE]: LocalShippingOutlinedIcon,
  [RECEIVING_WORKFLOW_ONSITE_CAPTURE]: CameraAltOutlinedIcon,
  [RECEIVING_WORKFLOW_ONSITE_MANIFEST]: FactCheckOutlinedIcon,
};

const colorMap = {
  receiving_assistant: {
    color: "#B7791F",
    backgroundColor: "rgba(183, 121, 31, 0.12)",
  },
  onsite_capture_to_warehouse_storage: {
    color: "#1976D2",
    backgroundColor: "rgba(25, 118, 210, 0.12)",
  },
  onsite_capture: {
    color: "#2E7D32",
    backgroundColor: "rgba(46, 125, 50, 0.12)",
  },
  onsite_manifest: {
    color: "#7B1FA2",
    backgroundColor: "rgba(123, 31, 162, 0.12)",
  },
};

const getIconComponent = (value) => iconMap[value] || WarehouseOutlinedIcon;

export default function ReceivingWorkflowSelectionModal({ open, onClose, currentValue, onSelect, cardOptions }) {
  const [selected, setSelected] = useState(currentValue);

  useEffect(() => {
    if (open) {
      setSelected(currentValue);
    }
  }, [currentValue, open]);

  const baseOptions = cardOptions || RECEIVING_WORKFLOW_CARD_OPTIONS;
  const options = useMemo(
    () =>
      baseOptions.map((option) => ({
        ...option,
        Icon: getIconComponent(option.value),
      })),
    [baseOptions]
  );

  const handleCancel = () => {
    setSelected(currentValue);
    onClose();
  };

  const handleSelect = (value) => {
    setSelected(value);
    onSelect(value);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={handleCancel}
      PaperProps={{ sx: { width: "620px", maxWidth: "92vw" } }}
    >
      <DialogContent sx={{ bgcolor: "#f4f6f8" }}>
        <DialogContentText>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box width={40} />
            <Typography fontSize={22} fontWeight={400} textAlign="center" color="text.primary">
              Select Mode
            </Typography>
            <IconButton color="textPrimary" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={1.5}>
            {options.map((option) => {
              const isSelected = selected === option.value;
              const Icon = option.Icon;
              const iconStyles = colorMap[option.value] || colorMap.receiving_assistant;

              return (
                <Paper
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  elevation={isSelected ? 2 : 0}
                  sx={{
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "transparent",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: isSelected ? "0 0 0 1px rgba(25, 118, 210, 0.08)" : "none",
                    "&:hover": {
                      borderColor: isSelected ? "primary.main" : "grey.300",
                    },
                  }}
                >
                  <Box p={2} display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: iconStyles.backgroundColor,
                        color: iconStyles.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography fontWeight={500} fontSize={15} color="text.primary">
                        {option.label}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}
